import { useState, useRef } from "react";

// ─── Design tokens (matches storage guide) ────────────────────────────────────
const C = {
  bg:"#0D1117", surface:"#161B22", card:"#1C2230", border:"#2D3748",
  text:"#E6EDF3", muted:"#8B949E", dim:"#4A5568",
  blue:"#58A6FF", blueDim:"#1C3A5E", blueGlow:"#1A3A5C",
  green:"#3FB950", greenDim:"#1A3A20",
  amber:"#D29922", amberDim:"#3A2C0A",
  red:"#F85149", redDim:"#3A1A1A",
  purple:"#BC8CFF", purpleDim:"#2D1F4A",
  teal:"#39D2C0", tealDim:"#0D2E2B",
  cyan:"#79C0FF", coral:"#FF7B72",
};

const s = {
  app:{ background:C.bg, minHeight:"100vh", color:C.text, fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", fontSize:14 },
  sidebar:{ width:240, background:C.surface, borderRight:`1px solid ${C.border}`, height:"calc(100vh - 70px)", position:"fixed", top:70, left:0, overflowY:"auto", zIndex:100 },
  main:{ marginLeft:240, minHeight:"100vh" },
  navItem:(a)=>({ display:"flex", alignItems:"center", gap:10, padding:"10px 16px", cursor:"pointer", borderRadius:8, margin:"2px 8px", background:a?C.blueGlow:"transparent", color:a?C.blue:C.muted, fontWeight:a?600:400, fontSize:13, transition:"all .18s", borderLeft:a?`3px solid ${C.blue}`:"3px solid transparent" }),
  section:{ padding:"32px 40px", maxWidth:1060, margin:"0 auto" },
  h1:{ fontSize:28, fontWeight:700, color:C.text, margin:"0 0 8px", letterSpacing:-0.5 },
  h2:{ fontSize:20, fontWeight:600, color:C.text, margin:"0 0 6px" },
  sub:{ color:C.muted, fontSize:14, lineHeight:1.7, margin:"0 0 24px" },
  card:{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:"20px 24px", marginBottom:16 },
  badge:(col)=>({ display:"inline-block", padding:"2px 10px", borderRadius:12, fontSize:11, fontWeight:700, background:col+"22", color:col, border:`1px solid ${col}44`, letterSpacing:0.3 }),
  code:{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, padding:"2px 7px", fontFamily:"'Fira Code','Consolas',monospace", fontSize:12, color:C.cyan },
  yamlBlock:{ background:"#0A0E14", border:`1px solid ${C.border}`, borderLeft:`3px solid ${C.blue}`, borderRadius:"0 8px 8px 0", padding:"16px 20px", fontFamily:"'Fira Code','Consolas',monospace", fontSize:12.5, lineHeight:2, overflowX:"auto", margin:"10px 0", whiteSpace:"pre" },
  btn:(col=C.blue,ghost=false)=>({ padding:"8px 18px", borderRadius:8, border:`1px solid ${col}55`, background:ghost?"transparent":col+"22", color:col, cursor:"pointer", fontSize:13, fontWeight:600, transition:"all .18s", display:"inline-flex", alignItems:"center", gap:6 }),
  infoBox:(col)=>({ background:col+"11", border:`1px solid ${col}33`, borderLeft:`3px solid ${col}`, borderRadius:"0 8px 8px 0", padding:"12px 16px", margin:"10px 0", fontSize:13, lineHeight:1.7 }),
  table:{ width:"100%", borderCollapse:"collapse", fontSize:13 },
  th:{ background:C.surface, color:C.muted, fontWeight:600, fontSize:11, textTransform:"uppercase", letterSpacing:0.8, padding:"10px 14px", textAlign:"left", borderBottom:`1px solid ${C.border}` },
  td:{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, color:C.text, verticalAlign:"top" },
};

// ─── YAML Highlighter ─────────────────────────────────────────────────────────
function Yaml({ children, accent }) {
  const ac = accent || C.blue;
  const lines = children.trim().split("\n");
  return (
    <div style={{...s.yamlBlock, borderLeftColor: ac}}>
      {lines.map((line, i) => {
        if (line.trimStart().startsWith("#")) return <div key={i}><span style={{color:C.dim}}>{line}</span></div>;
        const ci = line.indexOf(":");
        if (ci > -1 && !line.trimStart().startsWith("-")) {
          const key = line.slice(0, ci+1), val = line.slice(ci+1);
          const indent = key.match(/^(\s*)/)[1];
          return <div key={i}><span style={{color:C.dim}}>{indent}</span><span style={{color:ac}}>{key.slice(indent.length)}</span><span style={{color:C.green}}>{val}</span></div>;
        }
        if (line.trimStart().startsWith("- ")) {
          const ind = line.match(/^(\s*)/)[1];
          return <div key={i}><span style={{color:C.dim}}>{ind}</span><span style={{color:C.amber}}>{"- "}</span><span style={{color:C.green}}>{line.slice(ind.length+2)}</span></div>;
        }
        return <div key={i}><span style={{color:C.muted}}>{line}</span></div>;
      })}
    </div>
  );
}

// ─── Pod visual block ─────────────────────────────────────────────────────────
function PodBox({ name, status, color, containers, onClick, selected, small }) {
  const col = color || (status === "Running" ? C.green : status === "Pending" ? C.amber : status === "Failed" ? C.red : status === "Terminating" ? C.coral : C.muted);
  return (
    <div onClick={onClick} style={{ background: selected ? col+"22" : C.surface, border:`1.5px solid ${selected ? col : C.border}`, borderRadius:8, padding: small ? "8px 10px" : "10px 14px", cursor: onClick ? "pointer" : "default", transition:"all .2s", boxShadow: selected ? `0 0 12px ${col}33` : "none", minWidth: small ? 80 : 110 }}>
      <div style={{display:"flex", alignItems:"center", gap:6, marginBottom: small ? 2 : 4}}>
        <div style={{width:8, height:8, borderRadius:"50%", background:col, flexShrink:0}} />
        <span style={{fontSize: small ? 10 : 11, fontWeight:600, color:col, fontFamily:"monospace"}}>{name}</span>
      </div>
      {!small && status && <div style={{fontSize:10, color:C.dim, marginBottom:4}}>{status}</div>}
      {!small && containers && containers.map((c,i) => (
        <div key={i} style={{fontSize:10, background:C.card, borderRadius:4, padding:"2px 6px", margin:"2px 0", color:C.muted, fontFamily:"monospace"}}>{c}</div>
      ))}
    </div>
  );
}

// ─── Overview Section ─────────────────────────────────────────────────────────
function OverviewSection() {
  const concepts = [
    { icon:"🫁", name:"Pod", color:C.teal, analogy:"A single shift at work — one or more workers (containers) doing a job together, sharing a desk (network + storage). When the shift ends, everything is gone.", technical:"The smallest deployable unit in Kubernetes. One or more tightly-coupled containers that share network namespace, IP, and volumes. Ephemeral — if it dies, it's gone forever unless something recreates it.", when:"Direct pod creation is rare. Usually managed by a higher-level controller (Deployment, StatefulSet). Use bare pods only for one-off debugging or jobs.", fields:["spec.containers","spec.volumes","spec.restartPolicy","spec.nodeSelector","spec.serviceAccountName"] },
    { icon:"📋", name:"ReplicaSet", color:C.blue, analogy:"A staffing agency contract — 'always keep exactly 5 workers on the floor.' If one quits, hire another immediately. You deal with the agency, not individual workers.", technical:"Ensures a specified number of identical Pod replicas are running at all times. Watches Pods via label selectors. If a Pod dies, ReplicaSet creates a replacement. The 'guarantee' layer.", when:"Almost never created directly. Deployment creates and manages ReplicaSets for you. ReplicaSet alone has no rolling update or rollback capability.", fields:["spec.replicas","spec.selector","spec.template","status.readyReplicas","status.availableReplicas"] },
    { icon:"🚀", name:"Deployment", color:C.blue, analogy:"A restaurant manager — manages a team (ReplicaSet) of waiters (Pods). Can smoothly swap old staff for new ones (rolling update) without closing the restaurant. Can undo a bad hire (rollback).", technical:"The standard way to run stateless applications. Manages ReplicaSets. Provides declarative rolling updates, rollbacks, pause/resume. Every update creates a new ReplicaSet; the old one is kept for rollback.", when:"Stateless apps: web servers, APIs, frontends, background workers, microservices. Anything where pods are interchangeable and persistence is not needed between restarts.", fields:["spec.replicas","spec.strategy","spec.template","spec.selector","status.conditions"] },
    { icon:"🗃️", name:"StatefulSet", color:C.purple, analogy:"A hotel with room numbers — guests always return to the same numbered room, their belongings are always there, and they have a personal concierge (stable DNS). Rooms are never reassigned.", technical:"Like a Deployment but each Pod gets a sticky identity: a stable hostname (mysql-0, mysql-1), stable DNS, and its own PersistentVolumeClaim. Pods are created and deleted in strict order.", when:"Stateful apps: databases (MySQL, PostgreSQL, MongoDB), message queues (Kafka), search engines (Elasticsearch), distributed stores (etcd, Redis Cluster). Any app that cares about identity.", fields:["spec.serviceName","spec.replicas","spec.volumeClaimTemplates","spec.podManagementPolicy","spec.updateStrategy"] },
  ];
  return (
    <div style={s.section}>
      <div style={{marginBottom:32}}>
        <div style={{fontSize:11, color:C.blue, fontWeight:700, textTransform:"uppercase", letterSpacing:2, marginBottom:8}}>OpenShift Workloads — Complete Guide</div>
        <h1 style={s.h1}>Pod · ReplicaSet · Deployment · StatefulSet</h1>
        <p style={s.sub}>From a single container to enterprise-grade stateful clusters. Click any concept to explore it fully.</p>
      </div>

      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:32}}>
        {concepts.map(c => (
          <div key={c.name} style={{...s.card, borderTop:`3px solid ${c.color}`}}>
            <div style={{display:"flex", gap:12, alignItems:"flex-start"}}>
              <div style={{fontSize:36}}>{c.icon}</div>
              <div style={{flex:1}}>
                <div style={{fontWeight:700, color:c.color, fontSize:15, marginBottom:4}}>{c.name}</div>
                <div style={{...s.badge(c.color), marginBottom:10}}>{c.analogy.split("—")[0].trim()}</div>
                <p style={{color:C.muted, fontSize:13, lineHeight:1.7, margin:"8px 0"}}>{c.technical}</p>
                <div style={{...s.infoBox(c.color), margin:"8px 0"}}>
                  <strong style={{color:c.color}}>Use when:</strong> {c.when}
                </div>
                <div style={{marginTop:10}}>
                  <div style={{fontSize:11, color:C.dim, marginBottom:4}}>KEY FIELDS</div>
                  <div style={{display:"flex", flexWrap:"wrap", gap:4}}>{c.fields.map(f => <code key={f} style={{...s.code, fontSize:11}}>{f}</code>)}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Hierarchy diagram */}
      <div style={s.card}>
        <div style={{fontWeight:700, color:C.amber, marginBottom:16}}>⚡ The ownership hierarchy — who controls whom</div>
        <div style={{display:"flex", alignItems:"center", gap:0, overflowX:"auto", paddingBottom:8}}>
          {[
            {label:"You write YAML", sub:"kubectl apply", col:C.muted, icon:"👤"},
            {label:"Deployment", sub:"manages rollouts", col:C.blue, icon:"🚀"},
            {label:"ReplicaSet (new)", sub:"per-revision", col:C.cyan, icon:"📋"},
            {label:"ReplicaSet (old)", sub:"kept for rollback", col:C.dim, icon:"📋"},
            {label:"Pods ×N", sub:"actual containers", col:C.teal, icon:"🫁"},
          ].map((item, i) => (
            <div key={i} style={{display:"flex", alignItems:"center"}}>
              <div style={{textAlign:"center", minWidth:110}}>
                <div style={{fontSize:24, marginBottom:4}}>{item.icon}</div>
                <div style={{fontWeight:600, fontSize:12, color:item.col}}>{item.label}</div>
                <div style={{fontSize:10, color:C.dim}}>{item.sub}</div>
              </div>
              {i < 4 && <div style={{fontSize:20, color:C.border, margin:"0 4px", marginBottom:16}}>→</div>}
            </div>
          ))}
        </div>
        <div style={{...s.infoBox(C.amber), marginTop:8}}>
          StatefulSet has a <strong>direct relationship</strong> with Pods — no ReplicaSet in between. It also owns PVCs via <code style={s.code}>volumeClaimTemplates</code> — giving each Pod permanent, named storage.
        </div>
      </div>

      {/* Comparison table */}
      <div style={s.card}>
        <div style={{fontWeight:600, marginBottom:12}}>Quick comparison — choose the right workload</div>
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Feature</th>
            <th style={{...s.th, color:C.teal}}>Pod (bare)</th>
            <th style={{...s.th, color:C.blue}}>Deployment</th>
            <th style={{...s.th, color:C.purple}}>StatefulSet</th>
          </tr></thead>
          <tbody>
            {[
              ["Self-healing (auto-restart)","❌ No","✅ Yes","✅ Yes"],
              ["Stable pod names","❌ random hash","❌ random hash","✅ mysql-0, mysql-1"],
              ["Stable DNS per pod","❌ No","❌ No","✅ mysql-0.mysql.svc"],
              ["Own PVC per replica","❌ No","❌ No","✅ volumeClaimTemplates"],
              ["Rolling update","❌ No","✅ Yes (configurable)","✅ Yes (ordered)"],
              ["Rollback","❌ No","✅ oc rollout undo","⚠ Manual"],
              ["Scale up order","N/A","Parallel (any order)","Ordered 0→1→2"],
              ["Scale down order","N/A","Parallel (any order)","Ordered 2→1→0"],
              ["Use for","Debugging, Jobs","Web APIs, frontends","Databases, Kafka, etcd"],
            ].map(([feat, ...vals]) => (
              <tr key={feat}>
                <td style={{...s.td, fontWeight:500, color:C.text}}>{feat}</td>
                {vals.map((v,i) => <td key={i} style={{...s.td, color: v.startsWith("✅") ? C.green : v.startsWith("❌") ? C.red : v.startsWith("⚠") ? C.amber : C.muted}}>{v}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Pod Deep Dive ─────────────────────────────────────────────────────────────
function PodSection() {
  const [selPhase, setSelPhase] = useState("Running");
  const [selConcept, setSelConcept] = useState("containers");

  const phases = [
    { name:"Pending", col:C.amber, desc:"Pod accepted by API server but not yet scheduled to a node, OR containers are being pulled from registry. Can stay Pending if: no node has enough CPU/memory, PVC not bound, node selector doesn't match.", fix:"oc describe pod <name> → Events section. Look for: 'Insufficient cpu', 'did not match node selector', 'unbound PVC'." },
    { name:"Running", col:C.green, desc:"Pod is bound to a node. At least one container is running. Others may still be starting. This does NOT mean the app inside is healthy — just that the container process started.", fix:"Check readiness: oc get pod — READY column shows 1/1 means 1 of 1 containers is passing readiness probe." },
    { name:"Succeeded", col:C.blue, desc:"All containers exited with code 0 (success) and won't restart. Normal for batch Jobs and CronJobs. A web server pod should never reach this state.", fix:"Normal terminal state for Jobs. If a web server pod shows Succeeded, the process exited unexpectedly — check logs: oc logs <pod>." },
    { name:"Failed", col:C.red, desc:"All containers terminated, at least one exited with non-zero code or was killed by OOMKiller. restartPolicy=Never means it stays Failed.", fix:"oc logs <pod> --previous — see logs from the crashed container. Check exit code: oc describe pod → Last State section." },
    { name:"CrashLoopBackOff", col:C.coral, desc:"Container keeps crashing and Kubernetes keeps restarting it with exponential backoff (10s, 20s, 40s... up to 5 min). The most common error state you will encounter.", fix:"oc logs <pod> --previous — critical, shows logs from the crashed (previous) container, not the current retry. Common causes: wrong entrypoint, missing env var, bad config, OOM." },
    { name:"ImagePullBackOff", col:C.purple, desc:"Kubernetes can't pull the container image. Wrong image name, wrong tag, private registry with no pull secret, or registry is down.", fix:"oc describe pod → Events: 'Failed to pull image'. Check: image name typo, tag exists, imagePullSecrets configured for private registries." },
    { name:"OOMKilled", col:C.red, desc:"Container exceeded its memory limit. The Linux kernel OOM killer killed the process. Container restarts but will crash again unless either the memory limit is raised or the app's memory use is reduced.", fix:"oc describe pod → Last State: Reason: OOMKilled. Raise resources.limits.memory, OR add memory profiling to find the leak." },
  ];

  const concepts = {
    containers:{ label:"Containers in a Pod", icon:"📦", content:(
      <div>
        <p style={{color:C.muted, fontSize:13, lineHeight:1.7}}>A Pod can have multiple containers. They share the same network (localhost) and can share volumes. The main pattern is <strong style={{color:C.teal}}>sidecar</strong>: a helper container alongside the main app.</p>
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, margin:"12px 0"}}>
          {[{name:"Init containers",col:C.amber,desc:"Run sequentially BEFORE the app container starts. Must succeed (exit 0) or the Pod won't start. Use case: wait for DB to be ready, download config, run DB migrations.",badge:"runs first, blocks start"},{name:"App container",col:C.green,desc:"Your main application. Can have multiple — they start in parallel. Usually just one. This is what your users' traffic reaches.",badge:"main workload"},{name:"Sidecar container",col:C.blue,desc:"A helper that runs alongside your app. Classic examples: log shipper (Fluentd), service mesh proxy (Envoy/Istio), config watcher, cert rotator.",badge:"helper, runs alongside"},{name:"Ephemeral containers",col:C.purple,desc:"Injected into a running pod for debugging only. Not in the spec — added with 'oc debug'. Don't appear in pod YAML after creation.",badge:"debug only"}].map(c=>(
            <div key={c.name} style={{background:C.surface, border:`1px solid ${c.col}33`, borderRadius:8, padding:"12px 14px"}}>
              <div style={{fontWeight:600, color:c.col, marginBottom:4}}>{c.name}</div>
              <div style={{...s.badge(c.col), marginBottom:8, fontSize:10}}>{c.badge}</div>
              <div style={{color:C.muted, fontSize:12, lineHeight:1.6}}>{c.desc}</div>
            </div>
          ))}
        </div>
        <Yaml accent={C.teal}>{`spec:
  initContainers:
  - name: wait-for-db
    image: busybox
    command: ['sh', '-c', 'until nc -z postgres 5432; do sleep 2; done']
  containers:
  - name: app
    image: myapp:v2
    resources:
      requests:
        cpu: 100m
        memory: 128Mi
      limits:
        cpu: 500m
        memory: 512Mi
  - name: log-shipper
    image: fluent/fluent-bit
    # Sidecar: reads app logs, ships to Elasticsearch`}</Yaml>
      </div>
    )},
    probes:{ label:"Health probes", icon:"🩺", content:(
      <div>
        <p style={{color:C.muted, fontSize:13, lineHeight:1.7}}>Three probes tell Kubernetes the health state of a container. Without them, Kubernetes assumes the container is healthy as soon as it starts — which is almost never true for web apps.</p>
        {[{name:"Startup probe",col:C.amber,title:"Is the app done starting?",desc:"Runs FIRST. Until it passes, the other two probes are disabled. Protects slow-starting apps (JVM, legacy monoliths) from being killed before they're ready. Once it succeeds, it never runs again."},{name:"Readiness probe",col:C.blue,title:"Is the app ready to receive traffic?",desc:"Runs throughout the pod's life. If it fails, the pod is REMOVED from Service endpoints — traffic stops flowing to it. The pod stays alive. When it passes again, traffic resumes. Use for: circuit breaker, DB connection check."},{name:"Liveness probe",col:C.red,title:"Is the app stuck/deadlocked?",desc:"Runs throughout the pod's life. If it fails N times (failureThreshold), Kubernetes RESTARTS the container. Use sparingly — a bad liveness probe causes restart loops. Best for: deadlock detection."}].map(p=>(
          <div key={p.name} style={{...s.card, borderLeft:`3px solid ${p.col}`, margin:"0 0 10px"}}>
            <div style={{fontWeight:600, color:p.col, marginBottom:4}}>{p.name} — {p.title}</div>
            <div style={{color:C.muted, fontSize:13, lineHeight:1.6}}>{p.desc}</div>
          </div>
        ))}
        <Yaml accent={C.blue}>{`spec:
  containers:
  - name: app
    image: myapp:v2
    startupProbe:
      httpGet:
        path: /healthz
        port: 8080
      failureThreshold: 30      # 30 × 10s = 5 min to start
      periodSeconds: 10
    readinessProbe:
      httpGet:
        path: /ready
        port: 8080
      initialDelaySeconds: 5
      periodSeconds: 5
      failureThreshold: 3       # Remove from Service after 3 fails
    livenessProbe:
      httpGet:
        path: /healthz
        port: 8080
      initialDelaySeconds: 15
      periodSeconds: 20
      failureThreshold: 3       # Restart container after 3 fails`}</Yaml>
      </div>
    )},
    resources:{ label:"Resources & limits", icon:"⚖️", content:(
      <div>
        <p style={{color:C.muted, fontSize:13, lineHeight:1.7}}>Every container should declare resource requests and limits. Requests are used by the scheduler to find a node. Limits are enforced at runtime by Linux cgroups.</p>
        <div style={{...s.infoBox(C.amber)}}>
          <strong style={{color:C.amber}}>The hotel room analogy:</strong> <strong>Request</strong> = the room size you need (the hotel books a room of at least that size for you). <strong>Limit</strong> = the maximum you can use (you can't knock down walls to take the next room).
        </div>
        {[{label:"CPU: requests",col:C.blue,detail:"'100m' = 100 millicores = 0.1 CPU. Scheduler uses this to find a node with enough free CPU. Under-requested CPU → your pod competes for cycles and may be CPU-throttled. CPU throttling ≠ crash, but causes slow responses."},{label:"CPU: limits",col:C.red,detail:"Hard cap via Linux cgroups. If your container tries to use more, it's throttled (slowed, not killed). CPU limits are controversial — many teams set high limits or omit them entirely to avoid throttling."},{label:"Memory: requests",col:C.blue,detail:"Scheduler uses this to find a node with free memory. Under-request means your pod can be evicted if the node runs low on memory — even if the pod was working fine."},{label:"Memory: limits",col:C.red,detail:"Hard cap. If your container exceeds it, the Linux OOM killer kills it instantly. Container restarts. You see OOMKilled in 'oc describe pod'. Always set memory limits — uncapped containers can take down the entire node."}].map(r=>(
          <div key={r.label} style={{display:"flex", gap:12, padding:"8px 0", borderBottom:`1px solid ${C.border}`}}>
            <div style={{flex:"0 0 160px"}}><span style={s.badge(r.col)}>{r.label}</span></div>
            <div style={{color:C.muted, fontSize:13, lineHeight:1.6}}>{r.detail}</div>
          </div>
        ))}
        <Yaml accent={C.green}>{`resources:
  requests:
    cpu: 100m       # 0.1 CPU guaranteed to this container
    memory: 128Mi   # 128 MiB guaranteed, used for scheduling
  limits:
    cpu: 500m       # Max 0.5 CPU (throttled if exceeded)
    memory: 512Mi   # Max 512 MiB (OOMKilled if exceeded)

# Quality of Service classes (auto-assigned by K8s):
# Guaranteed: requests == limits (best for predictable workloads)
# Burstable:  requests < limits  (most common)
# BestEffort: no requests or limits (first evicted under pressure)`}</Yaml>
      </div>
    )},
    env:{ label:"Config & secrets", icon:"🔐", content:(
      <div>
        <p style={{color:C.muted, fontSize:13, lineHeight:1.7}}>Never hardcode config in your container image. OpenShift provides four clean ways to inject config and secrets at runtime.</p>
        {[{name:"env (literal)",col:C.muted,desc:"Direct key-value in the Pod spec. Fine for non-sensitive, non-changing config. Bad practice for secrets — the value is in plain text in etcd."},{name:"ConfigMap ref",col:C.blue,desc:"Mount a ConfigMap as env vars or a volume. ConfigMaps are for non-sensitive config: app settings, feature flags, URLs, port numbers. Changes require pod restart (env) or are auto-synced (volume mount)."},{name:"Secret ref",col:C.red,desc:"Mount a Secret as env vars or a volume. OpenShift encrypts secrets in etcd. Use for passwords, API keys, TLS certs. Never log env vars from secrets."},{name:"Downward API",col:C.purple,desc:"Inject pod metadata (pod name, namespace, node name, resource limits) as env vars. Useful for logging/tracing context without hardcoding anything."}].map(e=>(
          <div key={e.name} style={{...s.infoBox(e.col), marginBottom:8}}><strong style={{color:e.col}}>{e.name}:</strong> {e.desc}</div>
        ))}
        <Yaml accent={C.red}>{`env:
  # 1. Literal value (non-sensitive only)
  - name: PORT
    value: "8080"
  # 2. From ConfigMap
  - name: DB_HOST
    valueFrom:
      configMapKeyRef:
        name: app-config
        key: database.host
  # 3. From Secret (stored encrypted in etcd)
  - name: DB_PASSWORD
    valueFrom:
      secretKeyRef:
        name: db-secret
        key: password
  # 4. Downward API (pod's own metadata)
  - name: POD_NAME
    valueFrom:
      fieldRef:
        fieldPath: metadata.name`}</Yaml>
      </div>
    )},
  };

  return (
    <div style={s.section}>
      <h1 style={s.h1}>🫁 Pod — the atomic unit</h1>
      <p style={s.sub}>The hotel room where your containers live. Ephemeral by nature — treat pods as cattle, not pets. Never SSH into a prod pod to fix something; fix it in the image.</p>

      {/* Phase explorer */}
      <div style={s.card}>
        <div style={{fontWeight:600, color:C.teal, marginBottom:14, fontSize:15}}>Pod phase explorer — click any phase</div>
        <div style={{display:"flex", flexWrap:"wrap", gap:8, marginBottom:16}}>
          {phases.map(p => (
            <button key={p.name} style={s.btn(p.col, selPhase !== p.name)} onClick={() => setSelPhase(p.name)}>{p.name}</button>
          ))}
        </div>
        {(() => { const p = phases.find(x => x.name === selPhase); return (
          <div style={{background:C.surface, borderRadius:8, padding:"16px 20px", border:`1px solid ${p.col}33`}}>
            <div style={{fontWeight:700, color:p.col, marginBottom:8, fontSize:15}}>{p.name}</div>
            <div style={{color:C.muted, fontSize:13, lineHeight:1.7, marginBottom:12}}>{p.desc}</div>
            <div style={{...s.infoBox(C.blue)}}><strong style={{color:C.blue}}>How to diagnose:</strong> {p.fix}</div>
          </div>
        ); })()}
      </div>

      {/* Concept tabs */}
      <div style={s.card}>
        <div style={{display:"flex", gap:8, marginBottom:20, flexWrap:"wrap"}}>
          {Object.entries(concepts).map(([k,v]) => (
            <button key={k} style={s.btn(C.teal, selConcept !== k)} onClick={() => setSelConcept(k)}>
              {v.icon} {v.label}
            </button>
          ))}
        </div>
        {concepts[selConcept].content}
      </div>

      {/* Full pod YAML */}
      <div style={s.card}>
        <div style={{fontWeight:600, marginBottom:8}}>Complete pod YAML — annotated</div>
        <Yaml accent={C.teal}>{`apiVersion: v1
kind: Pod
metadata:
  name: my-app-pod
  namespace: prod
  labels:
    app: my-app          # Labels used by Services to route traffic
    version: v2
spec:
  serviceAccountName: my-app-sa   # RBAC identity for this pod
  initContainers:
  - name: wait-db
    image: busybox
    command: ['sh','-c','until nc -z postgres 5432; do sleep 2; done']
  containers:
  - name: app
    image: registry.example.com/my-app:v2
    ports:
    - containerPort: 8080
    env:
    - name: DB_HOST
      valueFrom:
        configMapKeyRef:
          name: app-config
          key: db.host
    - name: DB_PASS
      valueFrom:
        secretKeyRef:
          name: db-secret
          key: password
    resources:
      requests:
        cpu: 100m
        memory: 256Mi
      limits:
        cpu: 500m
        memory: 512Mi
    readinessProbe:
      httpGet:
        path: /ready
        port: 8080
      initialDelaySeconds: 10
      periodSeconds: 5
    livenessProbe:
      httpGet:
        path: /healthz
        port: 8080
      initialDelaySeconds: 30
      periodSeconds: 20
  restartPolicy: Always   # Always (default), OnFailure, Never`}</Yaml>
      </div>

      {/* Key CLI */}
      <div style={s.card}>
        <div style={{fontWeight:600, marginBottom:12}}>Essential pod commands</div>
        <table style={s.table}><tbody>
          {[["List pods","oc get pods -n <ns> -o wide"],["Watch pods live","oc get pods -n <ns> -w"],["Describe pod (events)","oc describe pod <name> -n <ns>"],["Logs (running)","oc logs <name> -n <ns> -f"],["Logs (previous crash)","oc logs <name> -n <ns> --previous"],["Shell into pod","oc exec -it <name> -- /bin/bash"],["Debug (ephemeral)","oc debug pod/<name>"],["Port-forward","oc port-forward pod/<name> 8080:8080"],["Delete pod (it restarts)","oc delete pod <name> -n <ns>"],["Force delete stuck pod","oc delete pod <name> --grace-period=0 --force"]].map(([d,c])=>(
            <tr key={d}><td style={{...s.td, color:C.muted, width:200}}>{d}</td><td style={s.td}><code style={s.code}>{c}</code></td></tr>
          ))}
        </tbody></table>
      </div>
    </div>
  );
}

// ─── ReplicaSet Section ───────────────────────────────────────────────────────
function ReplicaSetSection() {
  const [desired, setDesired] = useState(3);
  const [pods, setPods] = useState(["Running","Running","Running"]);
  const [log, setLog] = useState([]);
  const addLog = (msg, col=C.muted) => setLog(l => [...l.slice(-6), {msg, col, t: new Date().toLocaleTimeString()}]);

  const killPod = (i) => {
    const newPods = [...pods];
    newPods[i] = "Failed";
    setPods(newPods);
    addLog(`Pod ${i} failed! ReplicaSet detects: actual=${pods.filter(p=>p==="Running").length-1} < desired=${desired}`, C.red);
    setTimeout(() => {
      const replaced = [...newPods];
      replaced[i] = "Pending";
      setPods(replaced);
      addLog(`Creating replacement pod ${i}... (Pending)`, C.amber);
      setTimeout(() => {
        const running = [...replaced];
        running[i] = "Running";
        setPods(running);
        addLog(`Pod ${i} replacement is Running ✅ actual=${desired}`, C.green);
      }, 1200);
    }, 800);
  };

  const scale = (n) => {
    setDesired(n);
    if (n > pods.length) {
      const newPods = [...pods];
      for (let i = pods.length; i < n; i++) {
        newPods.push("Pending");
        addLog(`Scaling up: creating pod ${i}...`, C.amber);
      }
      setPods(newPods);
      setTimeout(() => {
        setPods(p => p.map(x => x === "Pending" ? "Running" : x));
        addLog(`Scale complete: ${n} pods running ✅`, C.green);
      }, 1000);
    } else if (n < pods.length) {
      addLog(`Scaling down: terminating ${pods.length - n} pods...`, C.amber);
      setPods(p => p.slice(0, n));
      addLog(`Scale complete: ${n} pods running ✅`, C.green);
    }
  };

  const phaseCol = {Running:C.green, Failed:C.red, Pending:C.amber};

  return (
    <div style={s.section}>
      <h1 style={s.h1}>📋 ReplicaSet — the guarantee layer</h1>
      <p style={s.sub}>The staffing agency. Its only job: count pods matching a label selector, compare to desired replicas, and act. It never stops watching. You rarely create these directly — Deployment does it for you.</p>

      {/* Interactive simulator */}
      <div style={s.card}>
        <div style={{fontWeight:600, color:C.blue, marginBottom:14, fontSize:15}}>ReplicaSet simulator — kill pods, scale replicas</div>
        <div style={{display:"flex", gap:16, alignItems:"flex-start", flexWrap:"wrap"}}>
          {/* Pod grid */}
          <div style={{flex:1, minWidth:300}}>
            <div style={{fontSize:12, color:C.muted, marginBottom:10}}>Click a pod to simulate failure → watch ReplicaSet heal it</div>
            <div style={{display:"flex", flexWrap:"wrap", gap:8, marginBottom:16}}>
              {pods.map((status, i) => (
                <PodBox key={i} name={`pod-${i}`} status={status} color={phaseCol[status]} onClick={() => status === "Running" && killPod(i)} />
              ))}
            </div>
            {/* Scale controls */}
            <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
              <span style={{color:C.muted, fontSize:13}}>Scale replicas:</span>
              {[1,2,3,4,5].map(n => (
                <button key={n} style={s.btn(n === desired ? C.blue : C.muted, n !== desired)} onClick={() => scale(n)}>{n}</button>
              ))}
            </div>
          </div>
          {/* Status + log */}
          <div style={{flex:1, minWidth:280}}>
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12}}>
              {[["desired",desired,C.blue],["actual",pods.filter(p=>p==="Running").length,C.green],["pending",pods.filter(p=>p==="Pending").length,C.amber],["failed",pods.filter(p=>p==="Failed").length,C.red]].map(([l,v,col])=>(
                <div key={l} style={{background:C.surface, borderRadius:8, padding:"10px 14px", border:`1px solid ${col}33`}}>
                  <div style={{fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:1}}>{l}</div>
                  <div style={{fontSize:22, fontWeight:700, color:col}}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{background:"#080C10", borderRadius:8, padding:"10px 14px", minHeight:100, fontFamily:"monospace", fontSize:11.5}}>
              {log.length===0 && <div style={{color:C.dim}}>Events appear here...</div>}
              {log.map((e,i) => <div key={i} style={{color:e.col, marginBottom:3}}><span style={{color:C.dim}}>{e.t} </span>{e.msg}</div>)}
            </div>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div style={s.card}>
        <div style={{fontWeight:600, marginBottom:12}}>How the ReplicaSet control loop works</div>
        {[{step:"1",title:"Watch phase",col:C.blue,desc:"ReplicaSet controller registers a watch on Pods matching its label selector (e.g. app=my-app, version=v2). The API server streams any changes — creation, deletion, phase changes — directly to the controller."},
          {step:"2",title:"Compare phase",col:C.amber,desc:"On every change event, the controller counts: how many pods with my labels are currently in Running or Pending state? If that count ≠ spec.replicas, it acts immediately."},
          {step:"3",title:"Act phase",col:C.green,desc:"Too few → create new pods using spec.template (the pod template embedded in the ReplicaSet). Too many → delete excess pods. The controller never touches pods it didn't create (different labels)."},
          {step:"4",title:"Convergence",col:C.teal,desc:"This loop runs continuously and reconciles state forever. Even if you manually delete a pod, the ReplicaSet notices within seconds and creates a replacement. The desired state always wins."},
        ].map(item => (
          <div key={item.step} style={{display:"flex", gap:14, marginBottom:12, paddingBottom:12, borderBottom:`1px solid ${C.border}`}}>
            <div style={{width:32, height:32, borderRadius:"50%", background:item.col+"22", border:`1px solid ${item.col}44`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, color:item.col, flexShrink:0, fontSize:14}}>{item.step}</div>
            <div><div style={{fontWeight:600, color:item.col, marginBottom:4}}>{item.title}</div><div style={{color:C.muted, fontSize:13, lineHeight:1.6}}>{item.desc}</div></div>
          </div>
        ))}
      </div>

      <div style={s.card}>
        <div style={{fontWeight:600, marginBottom:8}}>ReplicaSet YAML — what Deployment generates for you</div>
        <Yaml accent={C.blue}>{`apiVersion: apps/v1
kind: ReplicaSet
metadata:
  name: my-app-7d4b9c8f6   # Deployment adds hash suffix per revision
  labels:
    app: my-app
    pod-template-hash: 7d4b9c8f6   # Unique per revision
spec:
  replicas: 3
  selector:
    matchLabels:
      app: my-app
      pod-template-hash: 7d4b9c8f6  # Only owns pods with THIS hash
  template:                          # Pod template — copied from Deployment
    metadata:
      labels:
        app: my-app
        pod-template-hash: 7d4b9c8f6
    spec:
      containers:
      - name: app
        image: myapp:v2`}</Yaml>
      </div>
    </div>
  );
}

// ─── Deployment Section ───────────────────────────────────────────────────────
function DeploymentSection() {
  const [rolloutStep, setRolloutStep] = useState(0);
  const [strategy, setStrategy] = useState("RollingUpdate");
  const [selTopic, setSelTopic] = useState("rollout");
  const timer = useRef(null);

  const rollingSteps = [
    { label:"Start: all v1", old:[1,1,1,1], nw:[], desc:"4 pods running v1 (old ReplicaSet). No disruption yet." },
    { label:"Create 1 v2 pod", old:[1,1,1,1], nw:[1], desc:"New ReplicaSet (v2) creates its first pod. maxSurge=1 allows 5 total (4+1)." },
    { label:"Remove 1 v1 pod", old:[1,1,1,0], nw:[1], desc:"Once v2 pod is Ready, old ReplicaSet kills 1 v1 pod. Back to 4 total. maxUnavailable=1." },
    { label:"Create 2nd v2 pod", old:[1,1,1,0], nw:[1,1], desc:"New pod created (surge). 5 total. Traffic splits: 2/3 to v1, 1/3 to v2 ready pods." },
    { label:"Remove 2nd v1 pod", old:[1,1,0,0], nw:[1,1], desc:"Kill another v1 pod. 4 total. Gradual shift: 50/50 traffic." },
    { label:"Continue rolling…", old:[1,0,0,0], nw:[1,1,1], desc:"Pattern continues. v1 shrinks, v2 grows. Zero downtime throughout." },
    { label:"Complete!", old:[], nw:[1,1,1,1], desc:"All 4 pods running v2. Old ReplicaSet scaled to 0 but kept for rollback." },
  ];

  const recreateSteps = [
    { label:"Start: all v1", old:[1,1,1,1], nw:[], desc:"4 pods running v1." },
    { label:"Kill ALL v1 pods", old:[], nw:[], desc:"⚠️ ALL old pods terminated simultaneously. DOWNTIME STARTS NOW." },
    { label:"Create all v2 pods", old:[], nw:[1,1,1,1], desc:"4 new v2 pods created in parallel. App returns when they're all Ready. Downtime ends." },
  ];

  const steps = strategy === "RollingUpdate" ? rollingSteps : recreateSteps;
  const cur = steps[rolloutStep] || steps[0];

  const autoPlay = () => {
    let i = 0;
    clearInterval(timer.current);
    setRolloutStep(0);
    timer.current = setInterval(() => {
      i++;
      if (i >= steps.length) { clearInterval(timer.current); return; }
      setRolloutStep(i);
    }, 1200);
  };

  const topics = {
    rollout:{ label:"Rolling update & rollback", icon:"🔄", content:(
      <div>
        <div style={{display:"flex", gap:8, marginBottom:16, alignItems:"center", flexWrap:"wrap"}}>
          <span style={{color:C.muted, fontSize:13}}>Strategy:</span>
          <button style={s.btn(C.green, strategy!=="RollingUpdate")} onClick={()=>{setStrategy("RollingUpdate");setRolloutStep(0);}}>RollingUpdate (zero-downtime)</button>
          <button style={s.btn(C.red, strategy!=="Recreate")} onClick={()=>{setStrategy("Recreate");setRolloutStep(0);}}>Recreate (causes downtime)</button>
        </div>
        {/* Rollout visualiser */}
        <div style={{background:C.surface, borderRadius:8, padding:"16px 20px", marginBottom:12}}>
          <div style={{display:"flex", gap:24, marginBottom:12, flexWrap:"wrap"}}>
            <div>
              <div style={{fontSize:11, color:C.muted, marginBottom:6}}>OLD (v1)</div>
              <div style={{display:"flex", gap:4}}>
                {[0,1,2,3].map(i=><div key={i} style={{width:32,height:32,borderRadius:6,background:cur.old[i]?C.blue+"33":C.border,border:`1.5px solid ${cur.old[i]?C.blue:C.border}`,transition:"all .4s",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:cur.old[i]?C.blue:C.dim}}>v1</div>)}
              </div>
            </div>
            <div>
              <div style={{fontSize:11, color:C.muted, marginBottom:6}}>NEW (v2)</div>
              <div style={{display:"flex", gap:4}}>
                {[0,1,2,3].map(i=><div key={i} style={{width:32,height:32,borderRadius:6,background:cur.nw[i]?C.green+"33":C.border,border:`1.5px solid ${cur.nw[i]?C.green:C.border}`,transition:"all .4s",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:cur.nw[i]?C.green:C.dim}}>v2</div>)}
              </div>
            </div>
          </div>
          <div style={{fontWeight:600, color:strategy==="Recreate"&&rolloutStep===1?C.red:C.text, marginBottom:4}}>{cur.label}</div>
          <div style={{color:C.muted, fontSize:13}}>{cur.desc}</div>
        </div>
        <div style={{display:"flex", gap:8, marginBottom:16}}>
          <button style={s.btn(C.green)} onClick={autoPlay}>▶ Auto-play</button>
          <button style={s.btn(C.muted, true)} onClick={()=>{setRolloutStep(s=>Math.max(0,s-1))}} disabled={rolloutStep===0}>← Prev</button>
          <button style={s.btn(C.blue)} onClick={()=>{setRolloutStep(s=>Math.min(steps.length-1,s+1))}} disabled={rolloutStep===steps.length-1}>Next →</button>
          <button style={s.btn(C.dim,true)} onClick={()=>{clearInterval(timer.current);setRolloutStep(0);}}>↺</button>
          <div style={{marginLeft:"auto", display:"flex", gap:4, alignItems:"center"}}>
            {steps.map((_,i)=><div key={i} style={{width:7,height:7,borderRadius:"50%",background:i===rolloutStep?C.blue:i<rolloutStep?C.green:C.border,transition:"all .3s"}}/>)}
          </div>
        </div>
        <Yaml accent={C.blue}>{`spec:
  replicas: 4
  strategy:
    type: RollingUpdate
    rollingUpdate:
      maxSurge: 1          # Allow 1 extra pod during update (4+1=5 max)
      maxUnavailable: 1    # Allow 1 pod unavailable (minimum 3 always ready)
  # For Recreate (downtime accepted):
  # strategy:
  #   type: Recreate`}</Yaml>
        <div style={{...s.infoBox(C.amber)}}>
          <strong style={{color:C.amber}}>Rollback in one command:</strong><br/>
          <code style={s.code}>oc rollout undo deployment/my-app</code> — switches traffic back to previous ReplicaSet instantly. Old RS was kept at 0 replicas for exactly this reason.
        </div>
      </div>
    )},
    hpa:{ label:"Autoscaling (HPA)", icon:"📈", content:(
      <div>
        <p style={{color:C.muted, fontSize:13, lineHeight:1.7}}>HorizontalPodAutoscaler watches metrics and automatically adjusts spec.replicas on your Deployment. It's the difference between paying for peak capacity 24/7 or only when you need it.</p>
        {[{name:"CPU-based HPA",col:C.blue,desc:"Most common. HPA scales up when average CPU across pods exceeds targetCPUUtilizationPercentage. Scales down after cooldown period (default 5 min). Requires resource.requests.cpu to be set — HPA uses it to calculate utilization %."},
          {name:"Memory-based HPA",col:C.purple,desc:"Scales on memory utilization. Careful: memory doesn't release as quickly as CPU. Scaling down based on memory can lead to OOMKills on the newly downsized pods."},
          {name:"Custom metrics HPA",col:C.teal,desc:"Scale on any metric: requests per second (from Prometheus), queue depth (from Kafka), custom business metrics. Requires Prometheus Adapter or KEDA."},
        ].map(h=><div key={h.name} style={{...s.card,borderLeft:`3px solid ${h.col}`,margin:"0 0 10px"}}><div style={{fontWeight:600,color:h.col,marginBottom:4}}>{h.name}</div><div style={{color:C.muted,fontSize:13,lineHeight:1.6}}>{h.desc}</div></div>)}
        <Yaml accent={C.teal}>{`apiVersion: autoscaling/v2
kind: HorizontalPodAutoscaler
metadata:
  name: my-app-hpa
spec:
  scaleTargetRef:
    apiVersion: apps/v1
    kind: Deployment
    name: my-app
  minReplicas: 2      # Never go below this
  maxReplicas: 20     # Never exceed this
  metrics:
  - type: Resource
    resource:
      name: cpu
      target:
        type: Utilization
        averageUtilization: 70   # Scale up when avg CPU > 70%
  - type: Resource
    resource:
      name: memory
      target:
        type: AverageValue
        averageValue: 400Mi`}</Yaml>
      </div>
    )},
    affinity:{ label:"Node scheduling", icon:"📍", content:(
      <div>
        <p style={{color:C.muted, fontSize:13, lineHeight:1.7}}>By default, the scheduler places pods on any available node. These fields let you express preferences and requirements about where pods land.</p>
        {[{name:"nodeSelector (simple)",col:C.muted,desc:"Exact label match. Pod only schedules on nodes with these labels. Simplest, least flexible."},
          {name:"nodeAffinity (advanced)",col:C.blue,desc:"Like nodeSelector but supports operators: In, NotIn, Exists, DoesNotExist, Gt, Lt. Also supports 'preferred' (soft) rules vs 'required' (hard rules that prevent scheduling)."},
          {name:"podAntiAffinity",col:C.purple,desc:"Spread pods across nodes/zones. 'Don't put 2 replicas of this Deployment on the same node.' Critical for HA — if you have 3 replicas all on 1 node, losing that node kills your app."},
          {name:"topologySpreadConstraints",col:C.teal,desc:"More powerful than podAntiAffinity. 'Spread pods as evenly as possible across AZs, max 2 skew.' The modern way to ensure resilient pod distribution."},
        ].map(a=><div key={a.name} style={{...s.infoBox(a.col),marginBottom:8}}><strong style={{color:a.col}}>{a.name}:</strong> {a.desc}</div>)}
        <Yaml accent={C.purple}>{`spec:
  template:
    spec:
      # Spread replicas across availability zones
      topologySpreadConstraints:
      - maxSkew: 1
        topologyKey: topology.kubernetes.io/zone
        whenUnsatisfiable: DoNotSchedule
        labelSelector:
          matchLabels:
            app: my-app
      # Anti-affinity: no 2 replicas on same node
      affinity:
        podAntiAffinity:
          requiredDuringSchedulingIgnoredDuringExecution:
          - labelSelector:
              matchLabels:
                app: my-app
            topologyKey: kubernetes.io/hostname`}</Yaml>
      </div>
    )},
  };

  return (
    <div style={s.section}>
      <h1 style={s.h1}>🚀 Deployment — stateless app manager</h1>
      <p style={s.sub}>The restaurant manager. Handles the team (ReplicaSet), can swap staff without closing (rolling update), and can undo a bad hire (rollback). The right choice for 90% of workloads.</p>

      <div style={{display:"flex", gap:8, marginBottom:20, flexWrap:"wrap"}}>
        {Object.entries(topics).map(([k,v])=>(
          <button key={k} style={s.btn(C.blue, selTopic!==k)} onClick={()=>setSelTopic(k)}>{v.icon} {v.label}</button>
        ))}
      </div>
      {topics[selTopic].content}

      {/* Full YAML */}
      <div style={s.card}>
        <div style={{fontWeight:600, marginBottom:8}}>Complete Deployment YAML — production-ready</div>
        <Yaml accent={C.blue}>{`apiVersion: apps/v1
kind: Deployment
metadata:
  name: my-app
  namespace: prod
spec:
  replicas: 3
  selector:
    matchLabels:
      app: my-app         # Must match pod template labels
  strategy:
    type: RollingUpdate
    rollingUpdate:
      maxSurge: 1
      maxUnavailable: 0   # Zero-downtime: never kill before replacement ready
  template:
    metadata:
      labels:
        app: my-app
        version: v2
    spec:
      containers:
      - name: app
        image: registry.example.com/my-app:v2
        ports:
        - containerPort: 8080
        resources:
          requests:
            cpu: 100m
            memory: 256Mi
          limits:
            memory: 512Mi
        readinessProbe:
          httpGet:
            path: /ready
            port: 8080
          periodSeconds: 5
        livenessProbe:
          httpGet:
            path: /healthz
            port: 8080
          periodSeconds: 20
      affinity:
        podAntiAffinity:          # Spread across nodes for HA
          preferredDuringSchedulingIgnoredDuringExecution:
          - weight: 100
            podAffinityTerm:
              labelSelector:
                matchLabels:
                  app: my-app
              topologyKey: kubernetes.io/hostname`}</Yaml>
      </div>

      <div style={s.card}>
        <div style={{fontWeight:600, marginBottom:12}}>Essential deployment commands</div>
        <table style={s.table}><tbody>
          {[["Apply / update","oc apply -f deployment.yaml"],["Watch rollout","oc rollout status deployment/my-app"],["Rollback to previous","oc rollout undo deployment/my-app"],["Rollback to specific rev","oc rollout undo deployment/my-app --to-revision=3"],["See rollout history","oc rollout history deployment/my-app"],["Pause rollout","oc rollout pause deployment/my-app"],["Resume rollout","oc rollout resume deployment/my-app"],["Scale replicas","oc scale deployment/my-app --replicas=5"],["Update image","oc set image deployment/my-app app=myapp:v3"],["See all ReplicaSets","oc get rs -n <ns>"]].map(([d,c])=>(
            <tr key={d}><td style={{...s.td,color:C.muted,width:220}}>{d}</td><td style={s.td}><code style={s.code}>{c}</code></td></tr>
          ))}
        </tbody></table>
      </div>
    </div>
  );
}

// ─── StatefulSet Section ──────────────────────────────────────────────────────
function StatefulSetSection() {
  const [selReplica, setSelReplica] = useState(0);
  const [selTopic, setSelTopic] = useState("identity");
  const [scaleDir, setScaleDir] = useState(null);
  const [activeOrder, setActiveOrder] = useState([]);
  const timer2 = useRef(null);

  const animateOrder = (order, col) => {
    setActiveOrder([]);
    let i = 0;
    clearInterval(timer2.current);
    timer2.current = setInterval(() => {
      setActiveOrder(a => [...a, order[i]]);
      i++;
      if (i >= order.length) clearInterval(timer2.current);
    }, 600);
    setScaleDir(col);
  };

  const topics = {
    identity:{ label:"Stable identity", icon:"🪪", content:(
      <div>
        <p style={{color:C.muted, fontSize:13, lineHeight:1.7}}>Each StatefulSet pod gets a permanent, predictable identity. This is the single most important difference from a Deployment.</p>
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, margin:"16px 0"}}>
          {[0,1,2].map(i=>(
            <div key={i} onClick={()=>setSelReplica(i)} style={{...s.card,borderLeft:`3px solid ${i===selReplica?C.purple:C.border}`,background:i===selReplica?C.purpleDim:C.card,cursor:"pointer",padding:"14px 16px"}}>
              <div style={{fontSize:24,marginBottom:8}}>🗃️</div>
              <div style={{fontWeight:700,color:C.purple,marginBottom:2}}>mysql-{i}</div>
              <div style={{fontSize:11,color:C.muted,fontFamily:"monospace",marginBottom:6}}>mysql-{i}.mysql.prod.svc.cluster.local</div>
              <div style={{fontSize:11,color:C.green,fontFamily:"monospace"}}>data-mysql-{i} (50Gi PVC)</div>
              <div style={{fontSize:11,color:C.dim,marginTop:4}}>Node: {["worker-0","worker-2","worker-1"][i]}</div>
            </div>
          ))}
        </div>
        {[["Pod name",`mysql-${selReplica}`,C.purple,"Permanent. Never changes on restart. Always mysql-N for ordinal N."],["DNS hostname",`mysql-${selReplica}.mysql.prod.svc.cluster.local`,C.cyan,"Stable DNS via Headless Service. Other pods can always reach this exact pod by name."],["PVC",`data-mysql-${selReplica}`,C.green,"This pod's exclusive PVC. Created once, survives pod restarts and rescheduling. Never reassigned to another pod."],["Restart behavior","Recreated with same ordinal",C.blue,`If mysql-${selReplica} dies, a new pod is created and it gets the same name, same DNS, same PVC. Data is always there waiting.`]].map(([k,v,col,detail])=>(
          <div key={k} style={{padding:"8px 0",borderBottom:`1px solid ${C.border}`,display:"flex",gap:12}}>
            <div style={{flex:"0 0 130px",color:C.muted,fontSize:12}}>{k}</div>
            <div>
              <code style={{...s.code,color:col,display:"block",marginBottom:4}}>{v}</code>
              <div style={{fontSize:12,color:C.dim,lineHeight:1.5}}>{detail}</div>
            </div>
          </div>
        ))}
      </div>
    )},
    ordering:{ label:"Ordered operations", icon:"🔢", content:(
      <div>
        <p style={{color:C.muted, fontSize:13, lineHeight:1.7}}>Unlike Deployments that create/delete all pods in parallel, StatefulSets are strictly ordered. This is critical for distributed systems that need a leader, or need quorum to be maintained.</p>
        <div style={{display:"flex", gap:12, marginBottom:16, flexWrap:"wrap"}}>
          <button style={s.btn(C.green)} onClick={()=>animateOrder([0,1,2],C.green)}>▶ Scale UP (0→1→2)</button>
          <button style={s.btn(C.red)} onClick={()=>animateOrder([2,1,0],C.red)}>▼ Scale DOWN (2→1→0)</button>
          <button style={s.btn(C.dim,true)} onClick={()=>{clearInterval(timer2.current);setActiveOrder([]);setScaleDir(null);}}>↺</button>
        </div>
        <div style={{display:"flex", gap:12, alignItems:"center", margin:"16px 0"}}>
          {[0,1,2].map(i=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{textAlign:"center"}}>
                <div style={{width:60,height:60,borderRadius:10,border:`2px solid ${activeOrder.includes(i)?scaleDir:C.border}`,background:activeOrder.includes(i)?scaleDir+"22":C.surface,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",transition:"all .4s",boxShadow:activeOrder.includes(i)?`0 0 16px ${scaleDir}44`:"none"}}>
                  <div style={{fontSize:20}}>🗃️</div>
                  <div style={{fontSize:10,color:activeOrder.includes(i)?scaleDir:C.dim,fontWeight:600}}>mysql-{i}</div>
                </div>
                <div style={{fontSize:10,color:C.muted,marginTop:4}}>ordinal {i}</div>
              </div>
              {i<2&&<div style={{fontSize:20,color:C.dim}}>→</div>}
            </div>
          ))}
        </div>
        {[{name:"Scale up order",col:C.green,desc:"0 must be Running and Ready before 1 starts. 1 must be Running and Ready before 2 starts. This ensures the leader/primary (ordinal 0) is always elected before followers join."},
          {name:"Scale down order",col:C.red,desc:"2 is terminated first. Once gone, 1 is terminated. Finally 0. Reverse order protects quorum — you never remove the leader before the followers have left."},
          {name:"podManagementPolicy: Parallel",col:C.amber,desc:"Override the ordered behavior. All pods start/stop simultaneously — like a Deployment. Only use when your app doesn't need ordering (e.g. stateless app that just needs stable hostnames)."},
        ].map(o=><div key={o.name} style={{...s.infoBox(o.col),marginBottom:8}}><strong style={{color:o.col}}>{o.name}:</strong> {o.desc}</div>)}
      </div>
    )},
    headless:{ label:"Headless Service & DNS", icon:"🌐", content:(
      <div>
        <p style={{color:C.muted, fontSize:13, lineHeight:1.7}}>StatefulSets require a Headless Service (clusterIP: None). This is what provides stable per-pod DNS. Without it, the stable DNS hostnames don't work.</p>
        <div style={{...s.infoBox(C.blue), marginBottom:12}}>
          <strong style={{color:C.blue}}>Normal Service vs Headless Service:</strong><br/><br/>
          <strong>Normal:</strong> One VIP, load-balances across all pods. You get one IP. You can't reach individual pods.<br/><br/>
          <strong>Headless (clusterIP: None):</strong> DNS returns A records for each pod directly. You can reach <code style={s.code}>mysql-0.mysql.svc</code> specifically — crucial for primary/replica DB replication setup.
        </div>
        <Yaml accent={C.cyan}>{`# Required: Headless Service (enables per-pod DNS)
apiVersion: v1
kind: Service
metadata:
  name: mysql          # Must match StatefulSet spec.serviceName
  namespace: prod
spec:
  clusterIP: None      # 🔑 This makes it headless
  selector:
    app: mysql
  ports:
  - port: 3306
    name: mysql

# DNS records created:
# mysql-0.mysql.prod.svc.cluster.local → Pod IP of mysql-0
# mysql-1.mysql.prod.svc.cluster.local → Pod IP of mysql-1
# mysql-2.mysql.prod.svc.cluster.local → Pod IP of mysql-2
# mysql.prod.svc.cluster.local         → All pod IPs (round-robin)

# For client access (apps connecting to the DB):
# Create a SECOND normal Service pointing to mysql-0 only
# via label selector: statefulset.kubernetes.io/pod-name: mysql-0`}</Yaml>
      </div>
    )},
    pvctemplate:{ label:"Per-replica PVCs", icon:"💾", content:(
      <div>
        <p style={{color:C.muted, fontSize:13, lineHeight:1.7}}>The most powerful feature of StatefulSets: automatic PVC creation per pod. Each replica gets its own dedicated, named persistent storage.</p>
        <div style={{...s.infoBox(C.green), marginBottom:12}}>
          <strong style={{color:C.green}}>volumeClaimTemplates</strong> tells OpenShift: "When you create pod mysql-{"{i}"}, also create a PVC named <code style={s.code}>data-mysql-{"{i}"}</code> using this StorageClass." The PVC outlives the pod — if mysql-0 is deleted and recreated, it rebinds to <code style={s.code}>data-mysql-0</code> automatically.
        </div>
        <Yaml accent={C.green}>{`apiVersion: apps/v1
kind: StatefulSet
metadata:
  name: mysql
spec:
  replicas: 3
  serviceName: mysql          # Must match Headless Service name
  selector:
    matchLabels:
      app: mysql
  template:
    metadata:
      labels:
        app: mysql
    spec:
      containers:
      - name: mysql
        image: mysql:8.0
        env:
        - name: MYSQL_ROOT_PASSWORD
          valueFrom:
            secretKeyRef:
              name: mysql-secret
              key: root-password
        volumeMounts:
        - name: data
          mountPath: /var/lib/mysql
        resources:
          requests:
            cpu: 500m
            memory: 1Gi
          limits:
            memory: 2Gi
  volumeClaimTemplates:        # 🔑 Creates PVC per pod
  - metadata:
      name: data
    spec:
      accessModes: [ReadWriteOnce]
      storageClassName: gp3-rwo
      resources:
        requests:
          storage: 50Gi
# Results in:
# Pod mysql-0 → PVC data-mysql-0 (50Gi, permanently bound)
# Pod mysql-1 → PVC data-mysql-1 (50Gi, permanently bound)
# Pod mysql-2 → PVC data-mysql-2 (50Gi, permanently bound)`}</Yaml>
        <div style={{...s.infoBox(C.red)}}>
          <strong style={{color:C.red}}>⚠ PVCs survive StatefulSet deletion!</strong> If you <code style={s.code}>oc delete statefulset mysql</code>, the PVCs (and your data) are NOT deleted. You must manually delete PVCs: <code style={s.code}>oc delete pvc -l app=mysql</code>. This is intentional data protection.
        </div>
      </div>
    )},
  };

  return (
    <div style={s.section}>
      <h1 style={s.h1}>🗃️ StatefulSet — stateful app manager</h1>
      <p style={s.sub}>The hotel with room numbers. Every pod has a permanent identity, permanent storage, and a personal DNS address. The right choice for databases, message queues, and any distributed system that cares who it is.</p>

      <div style={{display:"flex", gap:8, marginBottom:20, flexWrap:"wrap"}}>
        {Object.entries(topics).map(([k,v])=>(
          <button key={k} style={s.btn(C.purple, selTopic!==k)} onClick={()=>setSelTopic(k)}>{v.icon} {v.label}</button>
        ))}
      </div>
      {topics[selTopic].content}

      {/* Comparison vs Deployment */}
      <div style={s.card}>
        <div style={{fontWeight:600, color:C.amber, marginBottom:12}}>StatefulSet vs Deployment — when to use which</div>
        <table style={s.table}>
          <thead><tr><th style={s.th}>Question</th><th style={{...s.th,color:C.blue}}>→ Deployment</th><th style={{...s.th,color:C.purple}}>→ StatefulSet</th></tr></thead>
          <tbody>
            {[["Does each pod need its own disk?","No — pods share or don't need PVCs","Yes — each replica needs its own PVC"],["Does pod identity matter?","No — pods are interchangeable","Yes — primary is mysql-0, replicas are mysql-1/2"],["Does startup order matter?","No — parallel is fine","Yes — leader must start before followers"],["Can pods be rescheduled freely?","Yes — any node, any time","Careful — ordinal must stay consistent"],["Examples","Web API, frontend, ML inference","MySQL, Kafka, Elasticsearch, MongoDB, etcd"]].map(([q,...vs])=>(
              <tr key={q}><td style={{...s.td,fontWeight:500,color:C.muted}}>{q}</td>{vs.map((v,i)=><td key={i} style={s.td}>{v}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={s.card}>
        <div style={{fontWeight:600, marginBottom:12}}>Essential StatefulSet commands</div>
        <table style={s.table}><tbody>
          {[["List StatefulSets","oc get statefulsets -n <ns>"],["Watch pods (ordered)","oc get pods -l app=mysql -w"],["Scale (ordered up/down)","oc scale statefulset mysql --replicas=5"],["Rolling update image","oc set image statefulset/mysql mysql=mysql:8.1"],["Update strategy",`oc patch sts mysql -p '{"spec":{"updateStrategy":{"type":"RollingUpdate"}}}'`],["List PVCs for StatefulSet","oc get pvc -l app=mysql"],["Delete SS (keeps PVCs!)","oc delete statefulset mysql"],["Delete SS + PVCs","oc delete statefulset mysql && oc delete pvc -l app=mysql"],["Shell into specific pod","oc exec -it mysql-0 -- mysql -u root -p"],["Logs from replica 0","oc logs mysql-0 -f"]].map(([d,c])=>(
            <tr key={d}><td style={{...s.td,color:C.muted,width:240}}>{d}</td><td style={s.td}><code style={s.code}>{c}</code></td></tr>
          ))}
        </tbody></table>
      </div>
    </div>
  );
}

// ─── Troubleshooting ──────────────────────────────────────────────────────────
function TroubleshootSection() {
  const [open, setOpen] = useState(null);
  const issues = [
    {title:"Pod in CrashLoopBackOff",color:C.red,icon:"💥",cause:"Container keeps starting and crashing. Kubernetes retries with exponential backoff. Could be: wrong entrypoint, missing env var, bad config file, OOM, app bug on startup.",steps:["oc logs <pod> --previous — CRITICAL: shows logs from the CRASHED container, not current retry","Look for the actual error (missing file, connection refused, OOM, panic)","oc describe pod <name> → Exit Code: non-zero means app error, Exit Code 137 = OOM","Check env vars: oc exec <pod> -- env (if pod starts briefly before crashing)","Verify ConfigMap/Secret exists: oc get cm <name>, oc get secret <name>"],cmd:`oc logs <pod> --previous -n <ns>
oc describe pod <pod> -n <ns>
# Check if Secret/ConfigMap missing:
oc get events -n <ns> --sort-by='.lastTimestamp'`},
    {title:"Deployment rollout stuck",color:C.amber,icon:"⏳",cause:"oc rollout status shows 'Waiting for rollout to finish' and hangs. New pods can't start (Pending or CrashLoopBackOff) so Deployment won't progress.",steps:["oc rollout status deployment/<name> -n <ns> — see which replica set is stuck","oc get pods -n <ns> — look for the new pods in bad state","oc describe pod <new-pod> — Events section will show the root cause","Common: readiness probe failing (wrong path/port), image pull error, PVC Pending, resource quota exceeded","Fix the root cause, then: oc rollout resume or oc rollout undo"],cmd:`oc rollout status deployment/my-app -n prod
oc get rs -n prod          # See old vs new ReplicaSet
oc get pods -n prod
oc describe pod <new-stuck-pod> -n prod
# Emergency rollback:
oc rollout undo deployment/my-app -n prod`},
    {title:"StatefulSet pod stuck in Pending",color:C.purple,icon:"🟣",cause:"StatefulSet is ordered — if mysql-0 is Pending, mysql-1 and mysql-2 will never start. Usually a PVC binding issue or resource constraint.",steps:["oc describe pod mysql-0 — check Events for resource or PVC issues","oc get pvc -n <ns> — is data-mysql-0 Bound or Pending?","If PVC Pending: oc describe pvc data-mysql-0 — no matching PV or wrong StorageClass?","Check resource availability: oc describe node — is there enough CPU/memory?","StatefulSet ordering: ALWAYS fix pod 0 first — pods start 0,1,2 in strict order"],cmd:`oc describe pod mysql-0 -n prod
oc get pvc -n prod
oc describe pvc data-mysql-0 -n prod
oc get sc    # Check StorageClass exists`},
    {title:"HPA not scaling up",color:C.teal,icon:"📊",cause:"HPA exists but pod count doesn't increase under load. Common causes: metrics-server not installed, resource.requests not set, min/max bounds hit.",steps:["oc describe hpa <name> — look at Conditions and Metrics sections","If 'unable to get metrics': metrics-server not running or not scraped","IMPORTANT: resources.requests.cpu MUST be set on the container — HPA calculates utilization as (usage/request)","Check: oc get pod -n openshift-monitoring — metrics pipeline running?","Verify actual CPU: oc top pods -n <ns>"],cmd:`oc describe hpa my-app-hpa -n prod
oc get pod -n openshift-monitoring
oc top pods -n prod
# Check metrics available:
oc get --raw /apis/metrics.k8s.io/v1beta1/namespaces/prod/pods`},
    {title:"Pods not spreading across nodes",color:C.blue,icon:"🌐",cause:"All 3 replicas landed on the same node. If that node dies, app goes down. Missing podAntiAffinity or topologySpreadConstraints.",steps:["oc get pods -o wide — check NODE column for all replicas","If all same node: add podAntiAffinity or topologySpreadConstraints to the Deployment spec","Note: if cluster only has 1 node (lab/dev), anti-affinity won't help — it requires multiple nodes","Check node count: oc get nodes","After adding affinity, rollout restarts: oc rollout restart deployment/<name>"],cmd:`oc get pods -o wide -n prod
oc get nodes
# Force a restart to trigger rescheduling:
oc rollout restart deployment/my-app -n prod`},
  ];
  return (
    <div style={s.section}>
      <h1 style={s.h1}>🔧 Troubleshooting workloads</h1>
      <p style={s.sub}>Real problems you will encounter, their root causes, diagnostic sequences, and resolution commands.</p>
      <div style={{...s.infoBox(C.blue), marginBottom:20, fontSize:14}}>
        <strong style={{color:C.blue}}>Golden rule:</strong> <code style={s.code}>oc describe pod &lt;name&gt;</code> + <code style={s.code}>oc logs &lt;name&gt; --previous</code> answer 90% of questions. The Events section of describe is where Kubernetes tells you exactly what went wrong.
      </div>
      {issues.map((issue,i)=>(
        <div key={i} style={{...s.card,borderLeft:`3px solid ${issue.color}`,marginBottom:10,cursor:"pointer"}} onClick={()=>setOpen(open===i?null:i)}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{display:"flex",gap:10,alignItems:"center"}}><span style={{fontSize:20}}>{issue.icon}</span><span style={{fontWeight:600,color:issue.color}}>{issue.title}</span></div>
            <span style={{color:C.muted,fontSize:18}}>{open===i?"−":"+"}</span>
          </div>
          {open===i&&<div style={{marginTop:14}}>
            <div style={s.infoBox(C.amber)}><strong>Root cause:</strong> {issue.cause}</div>
            <div style={{marginTop:12,marginBottom:8,fontWeight:600,color:C.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1}}>Diagnostic steps</div>
            <ol style={{paddingLeft:20,margin:0,color:C.muted,fontSize:13,lineHeight:2}}>
              {issue.steps.map((step,j)=><li key={j}>{step}</li>)}
            </ol>
            <Yaml accent={issue.color}>{issue.cmd}</Yaml>
          </div>}
        </div>
      ))}
    </div>
  );
}

// ─── CLI Reference ────────────────────────────────────────────────────────────
function CLISection() {
  const [copied, setCopied] = useState(null);
  const copy = (text, id) => { navigator.clipboard?.writeText(text); setCopied(id); setTimeout(() => setCopied(null), 2000); };
  const groups = [
    {label:"Pods",color:C.teal,cmds:[
      {d:"List pods (with node)",c:"oc get pods -n <ns> -o wide"},
      {d:"Watch pods live",c:"oc get pods -n <ns> -w"},
      {d:"Describe pod (events!)",c:"oc describe pod <name> -n <ns>"},
      {d:"Logs (running)",c:"oc logs <name> -n <ns> -f"},
      {d:"Logs (previous crash)",c:"oc logs <name> --previous -n <ns>"},
      {d:"Shell into container",c:"oc exec -it <name> -- /bin/bash"},
      {d:"Debug with ephemeral container",c:"oc debug pod/<name>"},
      {d:"Port-forward to local",c:"oc port-forward pod/<name> 8080:8080 -n <ns>"},
      {d:"Delete pod (controller recreates)",c:"oc delete pod <name> -n <ns>"},
      {d:"Top (CPU/mem usage)",c:"oc top pods -n <ns>"},
    ]},
    {label:"Deployments",color:C.blue,cmds:[
      {d:"Apply deployment",c:"oc apply -f deployment.yaml"},
      {d:"Watch rollout",c:"oc rollout status deployment/<name>"},
      {d:"Rollout history",c:"oc rollout history deployment/<name>"},
      {d:"Rollback to previous",c:"oc rollout undo deployment/<name>"},
      {d:"Rollback to revision N",c:"oc rollout undo deployment/<name> --to-revision=2"},
      {d:"Pause rollout",c:"oc rollout pause deployment/<name>"},
      {d:"Resume rollout",c:"oc rollout resume deployment/<name>"},
      {d:"Restart all pods",c:"oc rollout restart deployment/<name>"},
      {d:"Scale replicas",c:"oc scale deployment/<name> --replicas=5"},
      {d:"Update image",c:"oc set image deployment/<name> app=myapp:v3"},
    ]},
    {label:"StatefulSets",color:C.purple,cmds:[
      {d:"List StatefulSets",c:"oc get statefulsets -n <ns>"},
      {d:"Describe StatefulSet",c:"oc describe statefulset <name> -n <ns>"},
      {d:"Scale (ordered)",c:"oc scale statefulset <name> --replicas=3"},
      {d:"Rolling update image",c:"oc set image statefulset/<name> app=myapp:v3"},
      {d:"Watch ordered startup",c:"oc get pods -l app=<name> -w"},
      {d:"Shell into specific ordinal",c:"oc exec -it <name>-0 -- /bin/bash"},
      {d:"List StatefulSet PVCs",c:"oc get pvc -l app=<name>"},
      {d:"Delete SS (keeps PVCs)",c:"oc delete statefulset <name>"},
    ]},
    {label:"ReplicaSets",color:C.cyan,cmds:[
      {d:"List all ReplicaSets",c:"oc get rs -n <ns>"},
      {d:"See RS for a Deployment",c:"oc get rs -l app=<name> -n <ns>"},
      {d:"Describe RS",c:"oc describe rs <name> -n <ns>"},
    ]},
    {label:"Events & debugging",color:C.amber,cmds:[
      {d:"All events (sorted)",c:"oc get events -n <ns> --sort-by='.lastTimestamp'"},
      {d:"Warning events only",c:"oc get events -n <ns> --field-selector=type=Warning"},
      {d:"Top nodes",c:"oc top nodes"},
      {d:"Describe node",c:"oc describe node <node-name>"},
      {d:"Check resource quotas",c:"oc describe quota -n <ns>"},
    ]},
  ];
  return (
    <div style={s.section}>
      <h1 style={s.h1}>⌨️ CLI reference</h1>
      <p style={s.sub}>Every oc command you need, grouped by resource type. Click Copy to grab any command.</p>
      {groups.map(g=>(
        <div key={g.label} style={{marginBottom:24}}>
          <div style={{fontWeight:700,color:g.color,fontSize:13,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>{g.label}</div>
          {g.cmds.map((c,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
              <div style={{flex:"0 0 220px",color:C.muted,fontSize:13}}>{c.d}</div>
              <code style={{flex:1,fontFamily:"monospace",fontSize:12,color:C.cyan,background:C.surface,padding:"4px 10px",borderRadius:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.c}</code>
              <button style={{...s.btn(C.muted,true),padding:"4px 10px",fontSize:11}} onClick={()=>copy(c.c,i+g.label)}>{copied===i+g.label?"✓":"Copy"}</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Nav ──────────────────────────────────────────────────────────────────────
const NAV = [
  {id:"overview",icon:"🏗️",label:"Overview & Comparison"},
  {id:"pod",icon:"🫁",label:"Pod — atomic unit"},
  {id:"replicaset",icon:"📋",label:"ReplicaSet"},
  {id:"deployment",icon:"🚀",label:"Deployment"},
  {id:"statefulset",icon:"🗃️",label:"StatefulSet"},
  {id:"troubleshoot",icon:"🔧",label:"Troubleshooting"},
  {id:"cli",icon:"⌨️",label:"CLI Reference"},
];

export default function OpenshiftWorkloads() {
  const [active, setActive] = useState("overview");
  const panels = { overview:<OverviewSection/>, pod:<PodSection/>, replicaset:<ReplicaSetSection/>, deployment:<DeploymentSection/>, statefulset:<StatefulSetSection/>, troubleshoot:<TroubleshootSection/>, cli:<CLISection/> };
  return (
    <div style={s.app}>
      <div style={s.sidebar}>
        <div style={{padding:"20px 16px 12px",borderBottom:`1px solid ${C.border}`}}>
          <div style={{fontSize:11,color:C.blue,fontWeight:700,textTransform:"uppercase",letterSpacing:2,marginBottom:4}}>OpenShift</div>
          <div style={{fontSize:15,fontWeight:700,color:C.text}}>Workloads Mastery</div>
          <div style={{display:"flex",gap:4,marginTop:8,flexWrap:"wrap"}}>
            {["Pod","RS","Deploy","STS"].map(t=><span key={t} style={s.badge(C.blue)}>{t}</span>)}
          </div>
        </div>
        <nav style={{padding:"10px 0"}}>
          {NAV.map(n=>(
            <div key={n.id} style={s.navItem(active===n.id)} onClick={()=>setActive(n.id)}>
              <span style={{fontSize:16}}>{n.icon}</span>
              <span>{n.label}</span>
            </div>
          ))}
        </nav>
        <div style={{padding:"12px 16px",borderTop:`1px solid ${C.border}`}}>
          <div style={{fontSize:11,color:C.dim,lineHeight:1.6}}>Your cluster:<br/><code style={{color:C.muted,fontFamily:"monospace"}}>lab.ocp.local</code></div>
        </div>
      </div>
      <div style={s.main}>{panels[active]}</div>
    </div>
  );
}
